import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { prepareHeaders } from '../../configuration/tokenHandling/prepareHeaders';
import type {
    CommandResponse,
    ReclaimForAssetAlreadyInRentalCompanyCommand,
    ReclaimForAssetOnlyInRenterCompanyCommand,
    SellVehicleCommand,
    StartRentalCommand,
    StopRentalCommand,
} from '../../domain/model/commands.types';
import type { RentalCompany, RenterPartnerCompany } from '../../domain/model/rental-companies.types';
import type { Rental } from '../../domain/model/rentals.types';
import * as api from './generated/rental-api';
import {
    mapReclaimCommandToApi,
    mapRentalCompanyToDomain,
    mapRentalToDomain,
    mapRenterPartnerCompanyToDomain,
    mapSellVehicleCommandToApi,
    mapStartRentalCommandToApi,
    mapStopRentalCommandToApi,
} from './rental.mapper';

const RENTALS_TAG = 'Rentals';

const parseCommandResponse = (response: unknown): CommandResponse => {
    if (!response || typeof response !== 'object') {
        return {};
    }

    const { warnings } = response as { warnings?: unknown };
    return {
        warnings: Array.isArray(warnings)
            ? warnings.filter((warning): warning is string => typeof warning === 'string')
            : undefined,
    };
};

export const rentalApi = createApi({
    reducerPath: 'rentalApi',
    baseQuery: fetchBaseQuery({ baseUrl: import.meta.env.VITE_RENTAL_SERVICE_BASE_URL, prepareHeaders }),
    tagTypes: [RENTALS_TAG],
    endpoints: builder => ({
        fetchRentals: builder.query<Rental[], { accountId: string }>({
            query: ({ accountId }) => ({
                url: '/rentals',
                params: {
                    rental_company_account_id: accountId,
                },
            }),
            transformResponse: response => api.RentalList.parse(response).items.map(mapRentalToDomain),
            providesTags: (_, __, { accountId }) => [{ type: RENTALS_TAG, id: accountId }],
        }),
        fetchRentalCompanies: builder.query<RentalCompany[], void>({
            query: () => '/rental-companies',
            transformResponse: response => api.RentalCompanyList.parse(response).items.map(mapRentalCompanyToDomain),
        }),
        fetchRentalCompany: builder.query<RentalCompany, { accountId: string }>({
            query: ({ accountId }) => ({
                url: `/rental-companies/${accountId}`,
            }),
            transformResponse: response => mapRentalCompanyToDomain(api.RentalCompany.parse(response)),
        }),
        fetchRentalCompanyPartners: builder.query<RenterPartnerCompany[], { accountId: string }>({
            query: ({ accountId }) => ({
                url: `/rental-companies/${accountId}/partners`,
            }),
            transformResponse: response =>
                api.RenterPartnerList.parse(response).items.map(mapRenterPartnerCompanyToDomain),
        }),
        startRental: builder.mutation<CommandResponse, StartRentalCommand>({
            query: command => ({
                url: '/commands/start-rental',
                method: 'POST',
                body: mapStartRentalCommandToApi(command),
            }),
            transformResponse: parseCommandResponse,
            invalidatesTags: [RENTALS_TAG],
        }),
        stopRental: builder.mutation<CommandResponse, StopRentalCommand>({
            query: command => ({
                url: '/commands/stop-rental',
                method: 'POST',
                body: mapStopRentalCommandToApi(command),
            }),
            transformResponse: parseCommandResponse,
            invalidatesTags: [RENTALS_TAG],
        }),
        sellVehicle: builder.mutation<CommandResponse, SellVehicleCommand>({
            query: command => ({
                url: '/commands/sell-vehicle',
                method: 'POST',
                body: mapSellVehicleCommandToApi(command),
            }),
            transformResponse: parseCommandResponse,
            invalidatesTags: [RENTALS_TAG],
        }),
        reclaimAndStartRental: builder.mutation<
            CommandResponse,
            ReclaimForAssetAlreadyInRentalCompanyCommand | ReclaimForAssetOnlyInRenterCompanyCommand
        >({
            query: command => ({
                url: '/commands/reclaim',
                method: 'POST',
                body: mapReclaimCommandToApi(command),
            }),
            transformResponse: parseCommandResponse,
            invalidatesTags: [RENTALS_TAG],
        }),
    }),
});

export const {
    useFetchRentalsQuery,
    useFetchRentalCompaniesQuery,
    useFetchRentalCompanyQuery,
    useFetchRentalCompanyPartnersQuery,
    useStartRentalMutation,
    useStopRentalMutation,
    useSellVehicleMutation,
    useReclaimAndStartRentalMutation,
} = rentalApi;
